import { formatInTimeZone, zonedTimeToUtc } from "date-fns-tz";
import {
  AppointmentBookingParams,
  AppointmentBookingParamsOffering,
  SelectedAddOn,
} from "@Shape-Digital/kudzu-data/lib/types/actions";
import { SelectedOfferingSide } from "@Shape-Digital/kudzu-data/lib/types/common";
import { Coupon } from "../AppointmentBookingContext/types";

type CenterData = {
  id: string;
  timezoneOffsetMilliseconds: number;
  timezone: string;
};
type CenterServiceGroupId = "single-offering" | "package";
type CenterService = {
  value: string;
  groupId: CenterServiceGroupId;
  side?: SelectedOfferingSide;
  addOnIds?: string[];
  addOnQuantities?: Record<string, number>;
};
type AddOn = { id: string; quantity: number };

const toSelectedAddOns = (params: {
  addOnIds?: string[];
  addOnQuantities?: Record<string, number>;
}): SelectedAddOn[] => {
  const { addOnIds, addOnQuantities } = params;

  return (addOnIds || []).map((id) => ({
    id,
    quantity: addOnQuantities?.[id] ?? 1,
  }));
};

const getSelectedCenterServices = (centerServices: CenterService[]) => {
  const initialSelectedCenterServices: {
    selectedPackages: AppointmentBookingParams["selectedPackages"];
    selectedOfferings: AppointmentBookingParams["selectedOfferings"];
  } = {
    selectedPackages: [],
    selectedOfferings: [],
  };

  return centerServices.reduce((acc, centerService) => {
    const { groupId } = centerService;

    if (groupId === "package") {
      const { selectedPackages } = acc;
      const { value, addOnIds, addOnQuantities } = centerService;

      const selectedPackage = {
        id: value,
        selectedAddOns: toSelectedAddOns({ addOnIds, addOnQuantities }),
      };

      return {
        ...acc,
        selectedPackages: [...selectedPackages, selectedPackage],
      };
    }

    if (groupId === "single-offering") {
      const { selectedOfferings } = acc;
      const { value: id, side, addOnIds, addOnQuantities } = centerService;

      const offering: AppointmentBookingParamsOffering = {
        id,
        selectedAddOns: toSelectedAddOns({ addOnIds, addOnQuantities }),
      };

      if (side) {
        offering.selectedSide = side;
      }

      return {
        ...acc,
        selectedOfferings: [...selectedOfferings, offering],
      };
    }

    return acc;
  }, initialSelectedCenterServices);
};

const parseAppointment = (params: {
  center: CenterData;
  centerServices: CenterService[];
  addOns: AddOn[];
  dateTime: Date;
  updateAppointmentId?: string;
  overriddenPrice: number | null;
  overriddenDeposit: number | null;
  overriddenTime: string | null;
  coupon: Coupon | null;
}): AppointmentBookingParams => {
  const {
    center,
    centerServices,
    addOns,
    dateTime,
    updateAppointmentId,
    overriddenPrice,
    overriddenDeposit,
    overriddenTime,
    coupon,
  } = params;

  const { selectedPackages, selectedOfferings } =
    getSelectedCenterServices(centerServices);

  const finalDate = new Date(dateTime);

  if (overriddenTime) {
    const [hours, minutes] = overriddenTime.split(":").map(Number);
    finalDate.setHours(hours);
    finalDate.setMinutes(minutes);
  }

  const selectedAddOnIds: SelectedAddOn[] = addOns.map((addOn) => ({
    id: addOn.id,
    quantity: addOn.quantity,
  }));

  const dateUtc = zonedTimeToUtc(finalDate, center.timezone);

  const selectedStartISO = formatInTimeZone(
    dateUtc,
    "UTC",
    "yyyy-MM-dd'T'HH:mm:ssXXX",
  );

  return {
    centerId: center.id,
    selectedPackages,
    selectedOfferings,
    selectedAddOnIds,
    selectedStartISO,
    updateAppointmentId,
    overriddenPrice,
    overriddenDeposit,
    couponId: coupon?.id,
  };
};

export default parseAppointment;
